import { useCallback } from "react"
import { useLocation, useSearchParams } from "react-router"

import {
  stageSearchParamUpdates,
  useSearchParamStagingScope,
} from "@/utils/url/url-utils"

export interface UseGridUrlStateResult {
  replaceGridUrlState: (updates: Record<string, string | null>) => void
  values: Record<string, string | null>
}

/**
 * Identity of the named values in key order.
 * JSON keeps a value that contains "|" or ":" from matching a different set.
 * A missing value is empty, matching a cleared query key.
 */
export const gridUrlValuesKey = (
  keys: readonly string[],
  values: Record<string, string | null>
): string => JSON.stringify(keys.map((key) => values[key] ?? ""))

export const withGridUrlUpdates = (
  values: Record<string, string | null>,
  updates: Record<string, string | null>
): Record<string, string | null> => {
  const next = { ...values }

  for (const [key, value] of Object.entries(updates)) {
    next[key] = value == null || value === "" ? null : value
  }

  return next
}

/**
 * Reads named query keys and replaces the current history entry when they change.
 * A null or empty update deletes that key. Keys omitted from an update are left as they are.
 * A replace that would not change the query string is skipped so callers can
 * write the full key set on each render without extra history work.
 * Sequential replaces before the next render apply onto each other, so a sort
 * write is not dropped by a filter write in the same turn.
 */
export const useGridUrlState = (
  keys: readonly string[]
): UseGridUrlStateResult => {
  const location = useLocation()
  const searchParamStagingScope = useSearchParamStagingScope()
  const [searchParams, setSearchParams] = useSearchParams()
  const keysKey = keys.join("\0")

  const values: Record<string, string | null> = {}
  for (const key of keysKey.split("\0")) {
    if (!key) {
      continue
    }

    values[key] = searchParams.get(key)
  }

  const replaceGridUrlState = useCallback(
    (updates: Record<string, string | null>) => {
      stageSearchParamUpdates({
        committed: searchParams,
        locationKey: location.key,
        replace: true,
        scope: searchParamStagingScope,
        setSearchParams,
        updates,
      })
    },
    [location.key, searchParamStagingScope, searchParams, setSearchParams]
  )

  return { replaceGridUrlState, values }
}
